import { useState, useMemo, useRef, useEffect } from 'react';
import { BiGroup } from 'react-icons/bi';

import { imageVariantLink } from '@/common/media';
import { Avatar } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import Modal from '@/components/Modal/Modal';
import ParticipantBadge from '@/components/ParticipantBadge/ParticipantBadge';
import SelectionChip from '@/components/SelectionChip/SelectionChip';
import type { Person } from '@/store/persons/person.dto';

import styles from './PeopleSelectionModal.module.scss';

type PeopleSelectionModalProps = {
  people: Person[];
  selectedIds: string[];
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  triggerLabel?: string;
  modalTitle?: string;
  emptyMessage?: string;
  doneButtonLabel?: string;
  maxSelections?: number;
  compact?: boolean;
  /** Chips only show who is picked — no remove button, solid button-coloured background. */
  chipsReadOnly?: boolean;
  trigger?: React.ReactNode;
  label?: string;
  pinnedPeople?: Person[];
  showAddAll?: boolean;
  addAllLabel?: string;
  /** Confirmed with the done button — selections made in the modal can be committed here. */
  onDone?: () => void;
  /** Dismissed without confirming, so a caller batching its changes can drop them. */
  onCancel?: () => void;
  /**
   * Hands the modal's open state to the caller, for one that has something to settle
   * before the list shows — which tasks of a series a pick is for, say. The trigger then
   * only asks, through `onOpenChange`; the modal opens when `open` says so.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * One person offered as a button above the list rather than as a row in it — "Assign to
   * me" for an owner, who is not one of the crew the list holds. Picked, they show as a
   * chip like anyone else.
   */
  quickPick?: { label: string; person: Person };
  /**
   * Guests on the event who are not picked from the list: invited by email or phone, or
   * answered without an account. Shown beside the participants, read-only.
   */
  invitees?: {
    id: string;
    name: string;
    status: string;
    declined?: boolean;
    onRemove?: () => void;
  }[];
};

const PeopleSelectionModal = ({
  people,
  selectedIds,
  onSelect,
  onRemove,
  triggerLabel = 'Add People',
  modalTitle = 'Add Guests',
  emptyMessage = 'No people match. Please try a different search.',
  doneButtonLabel = 'Next',
  maxSelections,
  compact = false,
  chipsReadOnly = false,
  trigger,
  label,
  pinnedPeople = [],
  invitees = [],
  showAddAll = false,
  addAllLabel = 'Add All Participants',
  onDone,
  onCancel,
  open,
  onOpenChange,
  quickPick,
}: PeopleSelectionModalProps) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const isModalOpen = open ?? innerOpen;
  const setIsModalOpen = (next: boolean) => {
    if (open === undefined) setInnerOpen(next);
    onOpenChange?.(next);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedPeople = useMemo(
    () =>
      (quickPick ? [quickPick.person, ...people] : people).filter((person) =>
        selectedIds.includes(person.id),
      ),
    [people, selectedIds, quickPick],
  );

  const availablePeople = useMemo(() => {
    const unselected = people.filter((person) => !selectedIds.includes(person.id));

    if (!searchQuery.trim()) {
      return unselected;
    }

    const query = searchQuery.toLowerCase();

    return unselected.filter(
      (person) =>
        person.person_info?.first_name?.toLowerCase().includes(query) ||
        (person.person_info?.last_name &&
          person.person_info.last_name.toLowerCase().includes(query)) ||
        (person.services ?? []).some((personRole) =>
          personRole.role?.toLowerCase().includes(query),
        ),
    );
  }, [people, selectedIds, searchQuery]);

  const isMaxReached = maxSelections !== undefined && selectedIds.length >= maxSelections;

  const addablePeople = useMemo(
    () => people.filter((person) => !selectedIds.includes(person.id)),
    [people, selectedIds],
  );

  const handleAddAll = () => {
    addablePeople.forEach((person) => onSelect(person.id));
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setSearchQuery('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSearchQuery('');
  };

  const handleCancel = () => {
    onCancel?.();
    handleCloseModal();
  };

  const handleDone = () => {
    onDone?.();
    handleCloseModal();
  };

  const handlePersonSelect = (personId: string) => {
    if (!isMaxReached) {
      onSelect(personId);
      if (maxSelections !== undefined && selectedIds.length + 1 >= maxSelections) {
        handleCloseModal();
      } else {
        setSearchQuery('');
        inputRef.current?.focus();
      }
    }
  };

  const handleChipRemove = (personId: string) => {
    onRemove(personId);
    inputRef.current?.focus();
  };

  const handleInputContainerClick = () => {
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isModalOpen]);

  return (
    <div className={styles.container}>
      {trigger ? (
        <div onClick={handleOpenModal}>{trigger}</div>
      ) : (
        <div className={styles.root}>
          {label && <p className={styles.label}>{label}</p>}
          <button type="button" className={styles.triggerButton} onClick={handleOpenModal}>
            <span className={styles.triggerLabel}>
              {compact
                ? triggerLabel
                : selectedIds.length > 0
                  ? `${selectedIds.length} selected`
                  : triggerLabel}
            </span>
            <BiGroup color="#64748B" size={20} />
          </button>
        </div>
      )}

      <Modal open={isModalOpen} onClose={handleCancel} title={modalTitle}>
        <div className={styles.modalContent}>
          <div className={styles.inputContainer} onClick={handleInputContainerClick}>
            <div className={styles.chipsWrapper}>
              {selectedPeople.map((person) => {
                const displayName = person.person_info?.last_name
                  ? `${person.person_info.first_name} ${person.person_info.last_name}`
                  : person.person_info?.first_name || 'Unknown';
                return (
                  <SelectionChip
                    key={person.id}
                    label={displayName}
                    size="md"
                    className={chipsReadOnly ? styles.chipSolid : undefined}
                    onRemove={chipsReadOnly ? undefined : () => handleChipRemove(person.id)}
                  />
                );
              })}
              <input
                ref={inputRef}
                type="text"
                className={styles.searchInput}
                placeholder={selectedPeople.length === 0 ? 'Search people' : ''}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isMaxReached}
              />
            </div>
          </div>

          {showAddAll && (
            <ButtonOutlined
              type="button"
              onClick={handleAddAll}
              disabled={addablePeople.length === 0}
            >
              {addAllLabel}
            </ButtonOutlined>
          )}

          {quickPick && (
            <ButtonOutlined
              type="button"
              onClick={() => handlePersonSelect(quickPick.person.id)}
              disabled={isMaxReached || selectedIds.includes(quickPick.person.id)}
            >
              {quickPick.label}
            </ButtonOutlined>
          )}

          <div className={styles.peopleList}>
            {availablePeople.length === 0 ? (
              <p className={styles.noResults}>
                {searchQuery ? 'No people match. Please try a different search.' : emptyMessage}
              </p>
            ) : (
              availablePeople.map((person) => {
                const displayName = person.person_info?.last_name
                  ? `${person.person_info.first_name} ${person.person_info.last_name}`
                  : person.person_info?.first_name || 'Unknown';
                return (
                  <button
                    type="button"
                    key={person.id}
                    className={styles.personItem}
                    onClick={() => handlePersonSelect(person.id)}
                    disabled={isMaxReached}
                  >
                    <Avatar
                      src={imageVariantLink(person.avatar, 40)}
                      firstName={person.person_info?.first_name}
                      lastName={person.person_info?.last_name}
                      size={40}
                    />
                    <span className={styles.personName}>{displayName}</span>
                  </button>
                );
              })
            )}
          </div>

          <div className={styles.modalActions}>
            <Button onClick={handleDone}>{doneButtonLabel}</Button>
          </div>
        </div>
      </Modal>

      {!compact &&
        (selectedPeople.length > 0 || pinnedPeople.length > 0 || invitees.length > 0) && (
          <div className={styles.selectedPeople}>
            {pinnedPeople.map((person) => (
              <ParticipantBadge
                key={person.id}
                participantId={person.id}
                firstName={person.person_info?.first_name || 'Unknown'}
                lastName={person.person_info?.last_name}
                avatarUrl={imageVariantLink(person.avatar, 24)}
              />
            ))}
            {selectedPeople.map((person) => (
              <ParticipantBadge
                key={person.id}
                participantId={person.id}
                firstName={person.person_info?.first_name || 'Unknown'}
                lastName={person.person_info?.last_name}
                avatarUrl={imageVariantLink(person.avatar, 24)}
                onRemove={() => onRemove(person.id)}
              />
            ))}
            {invitees.map((invitee) => (
              <ParticipantBadge
                key={invitee.id}
                participantId={invitee.id}
                firstName={invitee.name}
                participantRole={invitee.status}
                declined={invitee.declined}
                onRemove={invitee.onRemove}
              />
            ))}
          </div>
        )}
    </div>
  );
};

export default PeopleSelectionModal;
export type { Person };
